/**
 * Post-processing state helper for SceneManager.
 *
 * Owns the per-frame math that converts transient "damage" / "card" pulses
 * into shader-uniform values, and debounces the VHS toggle so the last call
 * within a frame wins.
 *
 * Extracted from SceneManager so it can be unit-tested without booting a
 * WebGPU renderer (the SceneManager constructor wires three/webgpu, SkyMesh,
 * RenderPipeline, etc., all of which crash under jsdom).
 */

import { KNOCKBACK_DAMAGE_THRESHOLD } from '../../shared/constants';

export { KNOCKBACK_DAMAGE_THRESHOLD };

/** Minimal shape compatible with both `THREE.Uniform` and `three/tsl`'s `uniform()`. */
export interface UniformLike {
  value: number;
}

/**
 * Hard upper bounds for each cinematic post-FX uniform.
 *
 * The natural per-frame peak (when both pulses are saturated at their
 * built-in `damagePostPulse <= 1.5` / `cardPostPulse <= 1.2` caps) is well
 * under each of these limits — see `postProcessingState.test.ts`. The clamps
 * are a defensive ceiling so a future bug (e.g. a higher pulse cap, a new
 * trigger source, or a quality preset with `postIntensity > 1`) cannot wash
 * the screen out under sustained fire.
 *
 * Single-hit values (one default `triggerDamagePost(0.35)`) sit at roughly
 * 4-12% of these limits, so single-hit FX still look identical.
 */
export const POST_FX_LIMITS = {
  chromaticAmount: 0.02,
  grainIntensity: 0.05,
  vignetteDarkness: 0.5,
  bloomStrength: 1.5,
} as const;

/** Soft caps on the pulse accumulators themselves (preserved from original code). */
const DAMAGE_PULSE_CAP = 1.5;
const CARD_PULSE_CAP = 1.2;

/**
 * Shake-trigger sink — implemented by SceneManager. Extracted so the helper
 * doesn't depend on the WebGPU-flavored SceneManager (which can't boot under
 * jsdom).
 */
export interface SceneShakeSink {
  triggerShake(amount?: number, decayPerSec?: number): void;
}

/**
 * Convenience wrapper that fires a heavy camera kick when an incoming damage
 * value crosses {@link KNOCKBACK_DAMAGE_THRESHOLD}. The amount/decay are
 * intentionally hard-coded so the visual feel of "heavy hit" is tunable in
 * one place (here) rather than at every call site.
 */
export function triggerHeavyHitShake(sink: SceneShakeSink, damage: number): void {
  if (!Number.isFinite(damage) || damage < KNOCKBACK_DAMAGE_THRESHOLD) return;
  sink.triggerShake(0.3, 4);
}

/**
 * Shake state machine used by SceneManager. Owns the per-frame offset
 * sampling + decay so it can be unit-tested without booting WebGPU.
 *
 * The shake offset is sampled fresh per frame from a deterministic pseudo-
 * noise source seeded on `time`. Each axis is bounded by `±shakeAmount`,
 * which itself decays linearly toward 0 at `shakeDecayPerSec`. A second
 * `triggerShake` call takes the larger of (current amount, requested amount)
 * — overlapping heavy hits don't snowball but a smaller follow-up doesn't
 * cancel the rumble of a bigger one in flight.
 */
export class CameraShakeState {
  private shakeAmount = 0;
  private shakeDecayPerSec = 4;
  /** Last sampled offset (x, y, z). Each component lies in [-shakeAmount, +shakeAmount]. */
  readonly offset = { x: 0, y: 0, z: 0 };
  /** Monotonically increasing seed for the pseudo-noise sampler. */
  private t = 0;

  triggerShake(amount: number = 0.3, decayPerSec: number = 4): void {
    if (amount <= 0) return;
    if (amount > this.shakeAmount) this.shakeAmount = amount;
    this.shakeDecayPerSec = decayPerSec;
  }

  /**
   * Advance shake by `deltaSec`, sample a fresh offset, and decay the
   * amount. Should be called once per frame, before the renderer adds
   * `offset` to camera.position.
   */
  update(deltaSec: number): void {
    if (this.shakeAmount <= 0) {
      this.offset.x = 0;
      this.offset.y = 0;
      this.offset.z = 0;
      return;
    }
    this.t += deltaSec;
    // Three decorrelated sinusoids — same family as CameraController's
    // procedural shake. Each component lands in [-shakeAmount, +shakeAmount].
    const a = this.shakeAmount;
    this.offset.x = Math.sin(this.t * 47.0) * a;
    this.offset.y = Math.sin(this.t * 53.0 + 1.3) * a;
    this.offset.z = Math.sin(this.t * 41.0 + 2.7) * a;
    this.shakeAmount = Math.max(0, this.shakeAmount - this.shakeDecayPerSec * deltaSec);
  }

  amount(): number {
    return this.shakeAmount;
  }
}

/** Decay rates (units / second). Preserved from original code. */
const DAMAGE_DECAY_PER_SEC = 1.9;
const CARD_DECAY_PER_SEC = 1.35;

/**
 * U12 — Low-HP visual escalation thresholds.
 *
 * Below `LOW_HP_RAMP_START` (0.25) the screen begins darkening. The boost
 * ramps linearly to its peak at `LOW_HP_RAMP_END` (0.10) and stays peaked
 * for any fraction at or below 0.10. Below `LOW_HP_RED_TINT_THRESHOLD`
 * (0.10) a red-tint flag flips on so the shader pass can wash the frame.
 *
 * The vignette / chromatic boost is combined with the existing damage pulse
 * via `Math.max` so the two effects don't double up — whichever is bigger
 * this frame wins.
 */
const LOW_HP_RAMP_START = 0.25;
const LOW_HP_RAMP_END = 0.10;
const LOW_HP_RED_TINT_THRESHOLD = 0.10;
const LOW_HP_VIGNETTE_BOOST_MAX = 0.06;
const LOW_HP_CHROMATIC_BOOST_MAX = 0.0008;

export interface PostFxBindings {
  uChromaticAmount: UniformLike;
  uGrainIntensity: UniformLike;
  uVignetteDarkness: UniformLike;
  uEnableVHS: UniformLike;
  bloomStrength: UniformLike;
  bloomThreshold: UniformLike;
  basePostIntensity: number;
  baseBloomStrength: number;
  baseBloomThreshold: number;
}

export class PostFxState {
  private damagePulse = 0;
  private cardPulse = 0;
  /** Last-write-wins VHS toggle. `null` = no pending change this frame. */
  private pendingVHS: boolean | null = null;
  /**
   * U12 — local player's swarm HP fraction in [0, 1]. 1.0 = full health,
   * 0.0 = all drones dead. Updated each frame from `GameClient`.
   */
  private lowHpFraction = 1.0;
  /**
   * U12 — exposed boolean the SceneManager / cinematic shader pass can read
   * to enable the red-tint colour grade when HP is critical (< 0.10).
   * Recomputed every `update()`.
   */
  redTintEnabled = false;

  constructor(private readonly bindings: PostFxBindings) {}

  triggerDamagePulse(intensity: number): void {
    this.damagePulse = Math.min(DAMAGE_PULSE_CAP, this.damagePulse + intensity);
  }

  triggerCardPulse(intensity: number): void {
    this.cardPulse = Math.min(CARD_PULSE_CAP, this.cardPulse + intensity);
  }

  /** Debounced VHS toggle — the value is committed in `update()`. */
  queueVHS(enabled: boolean): void {
    this.pendingVHS = enabled;
  }

  /**
   * U12 — Update the local player's swarm HP fraction. Clamped to [0, 1];
   * `NaN` is treated as full health (1.0) so a transient missing-state
   * frame doesn't flash the red tint.
   */
  setLowHpFraction(fraction: number): void {
    if (!Number.isFinite(fraction)) {
      this.lowHpFraction = 1.0;
      return;
    }
    this.lowHpFraction = Math.max(0, Math.min(1, fraction));
  }

  /**
   * Per-frame tick. Decays pulses, recomputes uniform values, clamps every
   * output to `POST_FX_LIMITS`, and commits any queued VHS toggle.
   */
  update(deltaSec: number): void {
    this.damagePulse = Math.max(0, this.damagePulse - deltaSec * DAMAGE_DECAY_PER_SEC);
    this.cardPulse = Math.max(0, this.cardPulse - deltaSec * CARD_DECAY_PER_SEC);

    const {
      uChromaticAmount,
      uGrainIntensity,
      uVignetteDarkness,
      uEnableVHS,
      bloomStrength,
      bloomThreshold,
      basePostIntensity,
      baseBloomStrength,
      baseBloomThreshold,
    } = this.bindings;

    const damageBoost = this.damagePulse;
    const cardBoost = this.cardPulse;

    // Linear ramp 0 → 1 as HP fraction falls from 0.25 to 0.10. Above 0.25
    // the ramp is 0 (no contribution); below 0.10 it stays clamped at 1.
    const lowHpRamp =
      this.lowHpFraction >= LOW_HP_RAMP_START
        ? 0
        : this.lowHpFraction <= LOW_HP_RAMP_END
          ? 1
          : (LOW_HP_RAMP_START - this.lowHpFraction) /
            (LOW_HP_RAMP_START - LOW_HP_RAMP_END);
    const lowHpVignetteBoost = lowHpRamp * LOW_HP_VIGNETTE_BOOST_MAX;
    const lowHpChromaticBoost = lowHpRamp * LOW_HP_CHROMATIC_BOOST_MAX;
    this.redTintEnabled = this.lowHpFraction < LOW_HP_RED_TINT_THRESHOLD;

    // Damage-pulse contribution to chromatic, scaled by `basePostIntensity`
    // so the low-HP boost sees the same units when we Math.max them.
    const damageChromatic = damageBoost * 0.0014 * basePostIntensity;
    const damageVignette = damageBoost * 0.05;

    uChromaticAmount.value = Math.min(
      POST_FX_LIMITS.chromaticAmount,
      0.00018 * basePostIntensity +
        Math.max(damageChromatic, lowHpChromaticBoost) +
        cardBoost * 0.00035 * basePostIntensity,
    );
    uGrainIntensity.value = Math.min(
      POST_FX_LIMITS.grainIntensity,
      (0.0018 + damageBoost * 0.007 + cardBoost * 0.0018) * basePostIntensity,
    );
    uVignetteDarkness.value = Math.min(
      POST_FX_LIMITS.vignetteDarkness,
      0.03 + Math.max(damageVignette, lowHpVignetteBoost) + cardBoost * 0.02,
    );

    bloomStrength.value = Math.min(
      POST_FX_LIMITS.bloomStrength,
      baseBloomStrength + damageBoost * 0.12 + cardBoost * 0.18,
    );
    bloomThreshold.value = Math.max(0.58, baseBloomThreshold - cardBoost * 0.08);

    if (this.pendingVHS !== null) {
      uEnableVHS.value = this.pendingVHS ? 1.0 : 0.0;
      this.pendingVHS = null;
    }
  }
}
